/**
 * Pipeline Configuration
 *
 * Defines domain groups, work item types, and type-specific actions
 * for the action-oriented Pipeline workspace.
 */

import {
  Car,
  User,
  AlertTriangle,
  Wrench,
  FileText,
  Shield,
  FileWarning,
  Mail,
  Eye,
  UserPlus,
  CheckCircle,
  Phone,
  UserX,
  RefreshCw,
  ClipboardList,
  Edit,
  Fuel,
  Gauge,
  Calendar,
} from 'lucide-react';

/**
 * Priority color configuration
 */
export const PRIORITY_COLORS = {
  P0: 'bg-red-500',
  P1: 'bg-amber-500',
  P2: 'bg-blue-500',
  P3: 'bg-gray-400',
};

export const PRIORITY_TEXT_COLORS = {
  P0: 'text-red-600',
  P1: 'text-amber-600',
  P2: 'text-blue-600',
  P3: 'text-gray-500',
};

export const PRIORITY_BG_COLORS = {
  P0: 'bg-red-50',
  P1: 'bg-amber-50',
  P2: 'bg-blue-50',
  P3: 'bg-gray-50',
};

/**
 * Domain (entity_kind) groups
 * Groups work items by their related entity type
 */
export const PIPELINE_DOMAINS = {
  vehicle: {
    label: 'Vehicules',
    icon: Car,
    color: 'blue',
    order: 1,
  },
  driver: {
    label: 'Conducteurs',
    icon: User,
    color: 'purple',
    order: 2,
  },
  claim: {
    label: 'Sinistres',
    icon: AlertTriangle,
    color: 'amber',
    order: 3,
  },
  maintenance: {
    label: 'Maintenance',
    icon: Wrench,
    color: 'emerald',
    order: 4,
  },
  other: {
    label: 'Autres',
    icon: FileText,
    color: 'gray',
    order: 5,
  },
};

/**
 * Work item types with their actions
 * Each type defines:
 * - label: Display name
 * - domain: Parent domain (entity_kind)
 * - icon: Lucide icon component
 * - priority: Default priority level
 * - actions: Available actions for this type
 *
 * Action types:
 * - email: Generate and download .eml file
 * - workflow: Execute workflow transition on entity
 * - navigate: Navigate to entity detail view
 */
export const WORK_ITEM_TYPES = {
  insurance_missing: {
    label: 'Assurance manquante',
    domain: 'vehicle',
    icon: Shield,
    priority: 'P0',
    actions: [
      {
        key: 'email',
        label: 'Contacter courtier',
        icon: Mail,
        type: 'email',
        template: 'PIPELINE_INSURANCE_MISSING',
        color: 'blue',
      },
      {
        key: 'view',
        label: 'Voir vehicule',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
    ],
  },

  doc_expiring: {
    label: 'CT a renouveler',
    domain: 'vehicle',
    icon: FileWarning,
    priority: 'P1',
    actions: [
      {
        key: 'email',
        label: 'RDV garage',
        icon: Mail,
        type: 'email',
        template: 'PIPELINE_CT_REMINDER',
        color: 'blue',
      },
      {
        key: 'view',
        label: 'Voir vehicule',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
    ],
  },

  vehicle_unassigned: {
    label: 'Sans conducteur',
    domain: 'vehicle',
    icon: UserPlus,
    priority: 'P2',
    actions: [
      {
        key: 'assign',
        label: 'Assigner',
        icon: UserPlus,
        type: 'navigate',
        color: 'purple',
      },
      {
        key: 'view',
        label: 'Voir vehicule',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
    ],
  },

  license_expiring: {
    label: 'Permis a renouveler',
    domain: 'driver',
    icon: User,
    priority: 'P1',
    actions: [
      {
        key: 'email',
        label: 'Rappel',
        icon: Mail,
        type: 'email',
        template: 'PIPELINE_LICENSE_REMINDER',
        color: 'blue',
      },
      {
        key: 'view',
        label: 'Voir conducteur',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
    ],
  },

  driver_availability_alert: {
    label: 'Alerte disponibilite',
    domain: 'driver',
    icon: UserX,
    priority: 'P0', // Dynamic: P0 for unplanned, P1 for planned/unknown
    actions: [
      {
        key: 'call',
        label: 'Appeler',
        icon: Phone,
        type: 'action',
        action: 'CALL_DRIVER',
        color: 'green',
      },
      {
        key: 'email',
        label: 'Envoyer email',
        icon: Mail,
        type: 'email',
        template: 'DRIVER_AVAILABILITY_ALERT',
        color: 'blue',
      },
      {
        key: 'update',
        label: 'Actualiser',
        icon: RefreshCw,
        type: 'navigate',
        color: 'purple',
      },
      {
        key: 'view',
        label: 'Voir conducteur',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
    ],
  },

  claim_pending: {
    label: 'Declaration en attente',
    domain: 'claim',
    icon: AlertTriangle,
    priority: 'P0',
    actions: [
      {
        key: 'email',
        label: 'Email courtier',
        icon: Mail,
        type: 'email',
        template: 'CLAIM_DECLARATION',
        color: 'blue',
      },
      {
        key: 'declare',
        label: 'Declarer',
        icon: CheckCircle,
        type: 'workflow',
        action: 'envoyer-declaration',
        color: 'amber',
      },
      {
        key: 'view',
        label: 'Voir sinistre',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
    ],
  },

  // Workflow-generated claim email (created when workflow action generates an email)
  claim_email: {
    label: 'Email sinistre',
    domain: 'claim',
    icon: Mail,
    priority: 'P1',
    actions: [
      {
        key: 'download',
        label: 'Telecharger .eml',
        icon: Mail,
        type: 'email',
        // Template determined dynamically from work item title
        color: 'blue',
      },
      {
        key: 'done',
        label: 'Marquer envoye',
        icon: CheckCircle,
        type: 'resolve',
        resolution: 'email_sent',
        color: 'green',
      },
      {
        key: 'view',
        label: 'Voir sinistre',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
    ],
  },

  maintenance_due: {
    label: 'En retard',
    domain: 'maintenance',
    icon: Wrench,
    priority: 'P1',
    actions: [
      {
        key: 'email',
        label: 'Relancer',
        icon: Mail,
        type: 'email',
        template: 'PIPELINE_MAINTENANCE_REMINDER',
        color: 'blue',
      },
      {
        key: 'view',
        label: 'Voir maintenance',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
    ],
  },

  data_completeness: {
    label: 'Donnee manquante',
    domain: 'other', // Can be vehicle or driver - dynamic based on entity_kind
    icon: ClipboardList,
    priority: 'P2', // Dynamic: P1 for critical (phone, license), P2 for others
    actions: [
      {
        key: 'edit',
        label: 'Completer',
        icon: Edit,
        type: 'navigate',
        color: 'blue',
      },
      {
        key: 'snooze',
        label: 'Reporter',
        icon: RefreshCw,
        type: 'action',
        action: 'SNOOZE_7D',
        color: 'gray',
      },
      {
        key: 'view',
        label: 'Voir',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
    ],
  },

  resource_critical: {
    label: 'Niveau critique',
    domain: 'vehicle',
    icon: Gauge,
    priority: 'P0',
    actions: [
      {
        key: 'view',
        label: 'Voir vehicule',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
      {
        key: 'done',
        label: 'Resolu',
        icon: CheckCircle,
        type: 'action',
        action: 'DONE',
        color: 'green',
      },
    ],
  },

  resource_warning: {
    label: 'Niveau bas',
    domain: 'vehicle',
    icon: Gauge,
    priority: 'P1',
    actions: [
      {
        key: 'view',
        label: 'Voir vehicule',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
      {
        key: 'snooze',
        label: 'Reporter',
        icon: RefreshCw,
        type: 'action',
        action: 'SNOOZE_7D',
        color: 'gray',
      },
    ],
  },

  resource_due: {
    label: 'Echeance ressource',
    domain: 'vehicle',
    icon: Calendar,
    priority: 'P1',
    actions: [
      {
        key: 'view',
        label: 'Voir vehicule',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
      {
        key: 'done',
        label: 'Fait',
        icon: CheckCircle,
        type: 'action',
        action: 'DONE',
        color: 'green',
      },
    ],
  },

  resource_due_km: {
    label: 'Echeance km',
    domain: 'vehicle',
    icon: Fuel,
    priority: 'P2',
    actions: [
      {
        key: 'view',
        label: 'Voir vehicule',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
      {
        key: 'snooze',
        label: 'Reporter',
        icon: RefreshCw,
        type: 'action',
        action: 'SNOOZE_7D',
        color: 'gray',
      },
    ],
  },

  custom_note: {
    label: 'Note',
    domain: 'other',
    icon: FileText,
    priority: 'P2',
    actions: [
      {
        key: 'done',
        label: 'Termine',
        icon: CheckCircle,
        type: 'action',
        action: 'DONE',
        color: 'green',
      },
      {
        key: 'view',
        label: 'Voir',
        icon: Eye,
        type: 'navigate',
        color: 'gray',
      },
    ],
  },
};

/**
 * Get domain configuration by entity_kind or work item type
 */
export function getDomain(entityKindOrType) {
  // Check if it's a work item type first
  const typeConfig = WORK_ITEM_TYPES[entityKindOrType];
  if (typeConfig) {
    return PIPELINE_DOMAINS[typeConfig.domain] || PIPELINE_DOMAINS.other;
  }

  // Otherwise treat as entity_kind directly
  return PIPELINE_DOMAINS[entityKindOrType] || PIPELINE_DOMAINS.other;
}

/**
 * Get type configuration
 */
export function getTypeConfig(type) {
  return (
    WORK_ITEM_TYPES[type] || {
      label: type,
      domain: 'other',
      icon: FileText,
      priority: 'P2',
      actions: [{ key: 'view', label: 'Voir', icon: Eye, type: 'navigate', color: 'gray' }],
    }
  );
}

/**
 * Group work items by domain, then by type
 * Returns structure: { domain: { count, p0Count, byType: { type: items[] } } }
 */
export function groupItemsByDomain(items) {
  const grouped = {};

  // Initialize all domains
  Object.keys(PIPELINE_DOMAINS).forEach(domain => {
    grouped[domain] = {
      count: 0,
      p0Count: 0,
      p1Count: 0,
      byType: {},
    };
  });

  // Group items
  for (const item of items) {
    const typeConfig = WORK_ITEM_TYPES[item.type];
    const domain = typeConfig?.domain || item.entity_kind || 'other';
    const type = item.type || 'unknown';

    if (!grouped[domain]) {
      grouped[domain] = { count: 0, p0Count: 0, p1Count: 0, byType: {} };
    }

    if (!grouped[domain].byType[type]) {
      grouped[domain].byType[type] = [];
    }

    grouped[domain].byType[type].push(item);
    grouped[domain].count++;

    if (item.priority === 'P0') grouped[domain].p0Count++;
    if (item.priority === 'P1') grouped[domain].p1Count++;
  }

  // Sort items within each type by due_date
  Object.keys(grouped).forEach(domain => {
    Object.keys(grouped[domain].byType).forEach(type => {
      grouped[domain].byType[type].sort((a, b) => {
        // Null dates go last
        if (!a.due_date && !b.due_date) return 0;
        if (!a.due_date) return 1;
        if (!b.due_date) return -1;
        return a.due_date.localeCompare(b.due_date);
      });
    });
  });

  // Keep ALL domains for stable framework, sort by defined order
  // Empty domains remain visible to provide consistent UX
  const result = {};
  Object.entries(grouped)
    .sort((a, b) => {
      // Sort by defined order (stable framework)
      return (PIPELINE_DOMAINS[a[0]]?.order || 99) - (PIPELINE_DOMAINS[b[0]]?.order || 99);
    })
    .forEach(([domain, data]) => {
      result[domain] = data;
    });

  return result;
}

/**
 * Get domains with P0 items (for auto-expand)
 */
export function getDomainsWithP0(groupedItems) {
  return Object.entries(groupedItems)
    .filter(([, data]) => data.p0Count > 0)
    .map(([domain]) => `domain:${domain}`);
}

/**
 * Get types with P0 items within a domain (for auto-expand)
 */
export function getTypesWithP0(groupedItems) {
  const types = [];

  Object.entries(groupedItems).forEach(([, data]) => {
    Object.entries(data.byType).forEach(([type, items]) => {
      if (items.some(item => item.priority === 'P0')) {
        types.push(`type:${type}`);
      }
    });
  });

  return types;
}
